import { styled } from "@mui/material/styles";
import CN from "classnames";
import { forwardRef } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type ButtonLinkProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.AnchorHTMLAttributes<HTMLButtonElement>;

export type ButtonProps = ButtonLinkProps &
  React.PropsWithChildren<{
    prefixFaIcon?: IconDefinition;
    forwardedAs?: React.ElementType;
    type?: "submit" | "button";
    loading?: boolean;
    as?: React.ElementType;
    dataTestId?: string;
    hasContentAbove?: boolean;
  }>;

/**
 * @deprecated Use MUI <Button> instead
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      name,
      prefixFaIcon,
      type = "button",
      loading = false,
      dataTestId = name,
      hasContentAbove = false,
      className,
      ...props
    }: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => (
    <ButtonElement
      ref={ref}
      name={name}
      disabled={loading}
      type={type}
      data-testid={dataTestId}
      className={CN(className, hasContentAbove && "has-content-above")}
      {...props}
    >
      {!!prefixFaIcon && <FontAwesomeIcon className="-prefix" icon={prefixFaIcon} />}
      {children}
    </ButtonElement>
  )
);
Button.displayName = "Button";

const ButtonElement = styled("button")`
  // In case the Button is cast to an <a>, some reset styles which may not apply
  display: block;

  padding: 0.8rem 1.2rem 0.65rem 1.2rem;
  border: 0 solid rgba(0, 0, 0, 0.1);
  border-bottom-width: 2px;

  background: ${({ theme }) => theme.palette.dream.grayLight};
  color: ${({ theme }) => theme.palette.primary.main};
  border-radius: 8px;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 1rem;
  text-decoration: underline;
  text-align: center;

  cursor: pointer;
  transition:
    100ms linear background-color,
    100ms linear color;

  &:hover,
  &:active {
    background: ${({ theme }) => theme.palette.dream.grayMedium};
  }

  &:active {
    outline: none;
    border-top-width: 2px;
    border-bottom-width: 0;
  }

  &:disabled,
  &.-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    display: block;
    width: 100%;
    padding-top: 0.95rem;
    padding-bottom: 0.9rem;
    font-size: 1.1rem;
  }

  & > svg.-prefix {
    margin-left: -0.2em;
    margin-right: 0.8em;
  }

  &.has-content-above {
    margin-top: 1rem;
  }
`;

export const ButtonAsLink = styled(Button)<ButtonProps>`
  // In case the Button is cast to an <a>, some reset styles which may not apply
  display: inline;

  padding: 0;
  margin: 0;
  border: none;

  background: none;
  border-radius: 0;
  color: ${({ theme }) => theme.palette.primary.main};
  transition: none;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    width: unset;
  }

  &:hover {
    background: none;
    color: ${({ theme }) => theme.palette.dream.grayDark};
  }

  &:active {
    background: none;
  }

  & > svg.-prefix {
    margin-left: 0;
    margin-right: 0.5em;
  }
`;

export const SuccessButton = styled(Button)<ButtonProps>`
  background: ${({ theme }) => theme.palette.success.main};
  color: #fff;

  &:hover {
    background: ${({ theme }) => theme.palette.success.dark};
    color: #fff;
  }
`;

interface ButtonListProps {
  className?: string;
  withJustifiedRight?: boolean;
  withJustifiedTwoButtons?: boolean;

  [propsToElement: string]: unknown;
}

export const ButtonList = ({
  className = "",
  withJustifiedRight = false,
  withJustifiedTwoButtons = false,
  ...props
}: ButtonListProps) => (
  <ButtonListElement
    className={CN(
      className,
      withJustifiedRight && "with-justified-right",
      withJustifiedTwoButtons && "with-justified-two-buttons"
    )}
    {...props}
  />
);

const ButtonListElement = styled("div")`
  margin: 4px -1rem -1rem 0;

  display: flex;
  flex-wrap: wrap;
  align-items: stretch;

  &.with-justified-right {
    justify-content: flex-end;
  }

  &.with-justified-two-buttons {
    justify-content: space-between;
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    margin-right: 0;
    margin-bottom: -0.5rem;
    flex-direction: column;
    flex-wrap: nowrap;
    justify-content: flex-start;
  }

  &&& > * {
    margin: 0 1rem 1rem 0;

    ${({ theme }) => theme.breakpoints.down("sm")} {
      margin-right: 0;
      margin-bottom: 0.5rem;
    }
  }
`;
